import React, { useEffect, useState, useMemo, useCallback } from "react";
import { IoDocumentText } from "react-icons/io5";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import AccesoDirectoDocument from "../../molecules/empresas/AccesoDirectoDocument";
import { GET_FILE_TYPES } from "../../../api/apiEmpresas";
import Fuse from "fuse.js";
import Select from "react-select";
import { CREATE_DOES_NO_APPLY, GET_DOES_NO_APPLY_BY_CLIENT } from "../../../api/apiDoesNotApply";
import useModal from "../../../hooks/useModal";
import Modal from "../../molecules/Modal";
import ModalConfirm from "../../molecules/ModalConfirm";
import ModalBlockedTypes from "./ModalBlockedTypes";

const DocumentsByTable = ({ docs, empresa, tabla, user, loading, setLoading }) => {
  const [types, setTypes] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [doesNotApply, setDoesNotApply] = useState([]);
  const [code, setCode] = useState(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const { open, close, isOpen } = useModal();
  const { open: openEdit, close: closeEdit, isOpen: isOpenEdit } = useModal();

  const getFileTypes = async () => {
    try {
      const fileData = await GET_FILE_TYPES();
      setTypes(fileData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getFileTypes();
  }, []);

  const getDoesNotApply = useCallback(async () => {
    try {
      const response = await GET_DOES_NO_APPLY_BY_CLIENT(empresa.id);
      setCode(response.code);
      setDoesNotApply(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [empresa.id]);

  useEffect(() => {
    getDoesNotApply();
  }, [getDoesNotApply]);

  // Solo los types que existen en docs para esta tabla (intersección)
  const filteredTypes = useMemo(() => {
    if (!types || !docs || typeof docs !== "object") return [];
    return types.filter((type) => {
      if (type?.tableName === "sucursales" && empresa?.type === "NaturalPerson") return false;
      if (Object.prototype.hasOwnProperty.call(docs, type?.tableName)) return true;
      if (tabla === "archivo_permanente" && (type?.tableName === "sucursales" || type?.id === 122) && empresa?.type !== "NaturalPerson") return true;
      return false;
    });
  }, [types, docs, tabla, empresa?.type]);

  // tablenames bloqueados para esta empresa
  const blockedTableNames = useMemo(() => {
    const blocks = doesNotApply?.[0]?.blocks;
    if (!Array.isArray(blocks)) return new Set();
    return new Set(blocks.map((b) => b.tableName));
  }, [doesNotApply]);

  // Mapeo base de todos los documentos presentes en docs
  const allDocumentos = useMemo(() => {
    if (!types || !docs || typeof docs !== "object") return [];

    return types
      .filter((type) => {
        if (type?.tableName === "sucursales" && empresa?.type === "NaturalPerson") return false;
        if (Object.prototype.hasOwnProperty.call(docs, type?.tableName)) return true;
        if (tabla === "archivo_permanente" && (type?.tableName === "sucursales" || type?.id === 122) && empresa?.type !== "NaturalPerson") return true;
        return false;
      })
      .sort((a, b) => a?.id - b?.id)
      .map((type) => {
        const data = Array.isArray(docs[type.tableName]) ? docs[type.tableName] : [];
        const activeDocs = data.filter((doc) => doc.activo);

        return {
          name: type.name,
          tableName: type.tableName,
          path: `/clientes/empresas/${empresa?.id}/${tabla}/${type.tableName}`,
          icon: IoDocumentText,
          data: activeDocs,
          totalDocs: activeDocs.length,
          blocked: blockedTableNames.has(type.tableName),
        };
      });
  }, [types, docs, empresa, tabla, blockedTableNames]);

  const documentos = useMemo(() => allDocumentos.filter((d) => !d.blocked), [allDocumentos]);
  const documentosBloqueados = useMemo(() => allDocumentos.filter((d) => d.blocked), [allDocumentos]);

  const fuse = useMemo(
    () =>
      new Fuse(documentos, {
        keys: ["name"],
        threshold: 0.3,
      }),
    [documentos]
  );

  const options = documentos.map((doc) => ({
    value: doc.path,
    label: doc.name,
  }));

  const handleChange = (selected) => {
    setSelectedOption(selected);

    if (!selected) {
      setSearchResults([]);
      return;
    }

    const result = fuse.search(selected.label).map((res) => res.item);
    setSearchResults(result);
  };

  const listaFinal = selectedOption && searchResults.length > 0 ? searchResults : documentos;

  const handleCreate = async () => {
    try {
      setLoading(true);
      await CREATE_DOES_NO_APPLY({ clientId: empresa.id, blocks: [] });
      close();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isClearable
        placeholder="Buscar documento..."
      />

      {/* Lista de documentos activos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {listaFinal.map((document, index) => (
          <AccesoDirectoDocument
            label={document?.name}
            totalDocs={document?.totalDocs}
            href={document?.path}
            icon={document?.icon}
            key={index}
          />
        ))}
      </div>

      {/* Sección de documentos bloqueados (solo si hay y es admin) */}
      {documentosBloqueados.length > 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg overflow-hidden">
          {/* Toggle header */}
          <button
            onClick={() => setShowBlocked((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
          >
            <span className="text-sm font-medium text-slate-500">
              Tipos no aplicables ({documentosBloqueados.length})
            </span>
            {showBlocked
              ? <MdKeyboardArrowUp className="text-slate-400 text-xl" />
              : <MdKeyboardArrowDown className="text-slate-400 text-xl" />
            }
          </button>

          {/* Contenido expandible */}
          {showBlocked && (
            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 bg-slate-50/50">
              {documentosBloqueados.map((document, index) => (
                <div key={index} className="opacity-50 relative">
                  {/* Badge bloqueado */}
                  <span className="absolute top-2 right-2 z-10 text-[10px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                    No aplica
                  </span>
                  <AccesoDirectoDocument
                    label={document?.name}
                    totalDocs={document?.totalDocs}
                    href={document?.path}
                    icon={document?.icon}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Botones de gestión de reglas */}
      {user?.role !== "Client" && code === 201 ? (
        <button onClick={open}>Crear reglas de documentos</button>
      ) : user?.role !== "Client" && code === 200 ? (
        <button onClick={openEdit}>Modificar reglas de documentos</button>
      ) : user?.role !== "Client" && code === 500 ? (
        <p>Error al obtener las reglas de documentos</p>
      ) : null}

      {isOpen &&
        <ModalConfirm onConfirm={handleCreate} onClose={close} />
      }
      {isOpenEdit &&
        <Modal
          onClose={closeEdit}
          title="Reglas de documentos"
        >
          <ModalBlockedTypes
            doesNotApplyRecord={doesNotApply?.[0]}
            empresa={empresa}
            allowFiles={filteredTypes}
            onClose={closeEdit}
            onSaved={getDoesNotApply}
          />
        </Modal>
      }
    </div>
  );
};

export default DocumentsByTable;
